import React, { useState } from 'react';
import { AppModal } from './AppModal';
import { Button, Form, Input, Space, Radio, message } from '../';
import { useNotificationContext } from '../../providers/NotificationProvider';
import { ValidationErrors } from '../../constants';
import {
    useValidateMFAPhoneNumberCodeMutation,
    useSendMFAPhoneNumberCodeMutation,
    useSaveMFASettingsMutation,
} from '../../api/auth';

export const MFASetupModal = ({ open, onCancel, email, password }) => {
    const [form] = Form.useForm();

    const [selectedMethod, setSelectedMethod] = useState('email');
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isPhoneVerified, setIsPhoneVerified] = useState(false);

    const { notifySuccess, notifyError } = useNotificationContext();
    const [sendMFAPhoneNumberCode] = useSendMFAPhoneNumberCodeMutation();
    const [validateMFAPhoneNumberCode] = useValidateMFAPhoneNumberCodeMutation();
    const [saveMFASettings, { isLoading }] = useSaveMFASettingsMutation();

    const handleMethodChange = (e) => {
        const method = e.target.value;
        setSelectedMethod(method);
        form.setFieldsValue({ mfaMethod: method });
        setIsCodeSent(false);
        setIsPhoneVerified(false);
    };

    const handleSendCode = async () => {
        try {
            await form.validateFields(['phoneNumber']);
        } catch (error) {
            return;
        }

        try {
            // This triggers the form validation, if not valid it jumps to the catch block below
            const phoneNumber = form.getFieldValue('phoneNumber');
            await sendMFAPhoneNumberCode({ phoneNumber, email, password });
            setIsCodeSent(true);
        } catch (error) {
            notifyError({ message: 'Error sending verification code' });
            setIsCodeSent(false);
        }
    };

    const handleCheckCode = async () => {
        try {
            await form.validateFields(['mfaCode']);
        } catch (error) {
            return;
        }

        try {
            const { mfaCode, phoneNumber } = form.getFieldValue();
            await validateMFAPhoneNumberCode({ mfaCode, phoneNumber, email, password });
            notifySuccess({ message: 'Phone number verified successfully, you can now save your MFA settings' });
            setIsPhoneVerified(true);
        } catch (error) {
            notifyError({ message: 'Error verifying phone number' });
        }
    };

    const handleMFASetup = async () => {
        if (selectedMethod === 'sms' && !isPhoneVerified) {
            notifyError({ message: 'Please verify your phone number first' });
            return;
        }

        try {
            const { mfaMethod, phoneNumber } = await form.validateFields();
            await saveMFASettings({ email, password, mfaMethod, mfaPhoneNumber: phoneNumber }).unwrap();
            notifySuccess({
                message: 'MFA setup successful',
                description: 'Your MFA method has been set up successfully.',
                onClose: () => window.location.reload(),
            });
        } catch (error) {
            notifyError({ message: 'MFA setup failed', description: error.message });
        }
    };

    return (
        <AppModal title="Setup Two-Factor Authentication" onCancel={onCancel} open={open} footer={null}>
            <Form
                form={form}
                name="setup-mfa"
                layout="vertical"
                initialValues={{ mfaMethod: 'email' }}
                style={{ maxWidth: 600 }}
                onFinish={handleMFASetup}
            >
                <Form.Item
                    name="mfaMethod"
                    label="MFA Method"
                    rules={[{ required: true, message: 'Please select an MFA method' }]}
                >
                    <Radio.Group
                        options={[
                            { label: 'Email', value: 'email' },
                            { label: 'SMS', value: 'sms' },
                        ]}
                        value={selectedMethod}
                        onChange={handleMethodChange}
                    />
                </Form.Item>

                {selectedMethod === 'sms' && (
                    <>
                        <Form.Item
                            name="phoneNumber"
                            label="Phone Number"
                            rules={[
                                { required: true, message: 'Please enter your phone number' },
                                {
                                    pattern: /^[+][0-9]+$/,
                                    message: 'Phone number must contain a + sign and digits (no dashes or spaces)',
                                },
                            ]}
                        >
                            <Input placeholder="+12345678900" />
                        </Form.Item>

                        {!isCodeSent && (
                            <Form.Item>
                                <Button type="dashed" onClick={handleSendCode}>
                                    Send Verification Code
                                </Button>
                            </Form.Item>
                        )}

                        {isCodeSent && (
                            <>
                                <Form.Item
                                    name="mfaCode"
                                    label="Verification Code"
                                    rules={[{ required: true, message: 'Please input the verification code' }]}
                                >
                                    <Input placeholder="Enter the verification code" />
                                </Form.Item>

                                {!isPhoneVerified && (
                                    <Form.Item>
                                        <Button type="dashed" onClick={handleCheckCode}>
                                            Check Verification Code
                                        </Button>
                                    </Form.Item>
                                )}
                            </>
                        )}
                    </>
                )}

                <Form.Item style={{ textAlign: 'right' }}>
                    <Space>
                        <Button key="cancel" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button key="submit" type="primary" loading={isLoading} htmlType="submit">
                            Save
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </AppModal>
    );
};
